import React, { FC, Fragment, useState, useEffect } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { PencilIcon } from '@heroicons/react/24/outline';

export interface VisibilitySettings {
  production: boolean;
  consumption: boolean;
  batteryCharge: boolean;
  rewards: boolean;
}

interface DashboardEditMenuProps {
  visibilitySettings: VisibilitySettings;
  onVisibilityChange: (settings: VisibilitySettings) => void;
}

export const DashboardEditMenu: FC<DashboardEditMenuProps> = ({ 
  visibilitySettings, 
  onVisibilityChange 
}) => {
  const [settings, setSettings] = useState<VisibilitySettings>(visibilitySettings);

  // Update local state when prop changes
  useEffect(() => {
    setSettings(visibilitySettings);
  }, [visibilitySettings]);

  const handleToggle = (key: keyof VisibilitySettings) => {
    const newSettings = {
      ...settings,
      [key]: !settings[key]
    };
    setSettings(newSettings);
    onVisibilityChange(newSettings);
  };

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            className={`flex items-center rounded-lg bg-gray-100 dark:bg-[#1D1D1D] border border-gray-300 dark:border-[#292929] p-1.5 hover:bg-gray-200 dark:hover:bg-[#252525] transition-colors focus:outline-none ${
              open ? 'bg-gray-200 dark:bg-[#252525]' : ''
            }`}
            aria-label="Edit Dashboard"
          >
            <PencilIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute right-0 z-10 mt-2 w-64 transform px-0 sm:px-0">
              <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                <div className="relative bg-white dark:bg-[#1D1D1D] p-4 border border-gray-200 dark:border-[#292929]">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    Dashboard Configuration
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Production</span>
                      <Toggle 
                        enabled={settings.production} 
                        onChange={() => handleToggle('production')} 
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Consumption</span>
                      <Toggle 
                        enabled={settings.consumption} 
                        onChange={() => handleToggle('consumption')} 
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Battery Charge</span>
                      <Toggle 
                        enabled={settings.batteryCharge} 
                        onChange={() => handleToggle('batteryCharge')} 
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Rewards</span>
                      <Toggle 
                        enabled={settings.rewards} 
                        onChange={() => handleToggle('rewards')} 
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

// Toggle switch component
interface ToggleProps {
  enabled: boolean;
  onChange: () => void;
}

const Toggle: FC<ToggleProps> = ({ enabled, onChange }) => {
  return (
    <button
      type="button"
      className={`relative inline-flex h-6 w-11 items-center rounded-full ${
        enabled ? 'bg-[#00FF84]' : 'bg-gray-300 dark:bg-[#292929]'
      }`}
      onClick={onChange}
      aria-pressed={enabled}
    >
      <span className="sr-only">Toggle visibility</span>
      <span
        className={`${
          enabled ? 'translate-x-6' : 'translate-x-1'
        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
      />
    </button>
  );
};

export default DashboardEditMenu; 